// User-projection API fns — reads carved verbatim from `src/api/scribeAPIV2.ts`
// (which re-exports name-intact) with IP2 (ARCFE-956); the eight user-record
// writes carved from both hubs with D1 (ARCFE-1037, section below). The IP2
// five were `@domain REVIEW` in the hub; the carve-time ruling (same P0.4
// class as `updateOrganizationMemberRole` → org-teams) assigns them to
// workspace/identity-projection: each serves exactly one identity-claimed
// hook and reads the user's own config/data projection. Transport comes from
// the `@heidi/api` configured-client registry (E5) — behaviour byte-identical.

import { getScribeHttpClients } from '@heidi/api/client';
import { MFAMethod, type OnboardingTask, type Region } from '@heidi/schema/ml-scribe';
import {
  type CapabilityAutocompleteResponse,
  type CapabilityListResponse,
  type CapabilityWithUserConfigListResponse,
  type CreateUserRequest,
  type EffectiveSessionTimeout,
  type PendingSubStep,
  type PersonalizationDataResponse,
  type SelfOnboardRequest,
  type SelfOnboardResponse,
  type SnippetsResponse,
  type UpdateUserRequest,
  type UserResponse,
} from '../../contracts';
import { type WorkspaceHttpClient } from '../config';

const http = (): WorkspaceHttpClient => getScribeHttpClients().scribeHttpV2;

/**
 * The v1 client. `getUserByToken` reads the v1 `/user/` record, and only the
 * v1 request interceptor honours `explicitKindeToken` (see the field doc in
 * `packages/api/src/client/types.ts`).
 */
const httpV1 = (): WorkspaceHttpClient => getScribeHttpClients().scribeHttp;

/**
 * Fetch the current user's profile authenticated with an explicit bearer
 * token, bypassing the default Redux-token resolution. The backend verifies
 * the token itself (whichever issuer signed it) and returns the account it
 * actually resolves to.
 *
 * Used to corroborate that a freshly rotated token and a locally cached user
 * id name the same account, for auth providers whose token carries no
 * client-decodable account claim — see `corroborateViaCurrentUserLookup` in
 * `src/providers/chronicle-bulk-sync-provider/hooks/use-chronicle-bulk-sync-startup.ts`.
 *
 * @domain workspace
 */
export const getUserByToken = (token: string) => {
  return httpV1().get<UserResponse>(`/user/`, { explicitKindeToken: token });
};

/** @domain workspace */
export const getUserSnippets = () => {
  return http().get<SnippetsResponse>(`/snippets`);
};

/** @domain workspace */
export const getUserPersonalizations = () => {
  return http().get<PersonalizationDataResponse>(`/personalizations`);
};

/**
 * Get all capability definitions
 * @domain workspace
 */
export const getCapabilities = () => {
  return http().get<CapabilityListResponse>('/capabilities');
};

/**
 * Get user's capability configurations
 * @domain workspace
 */
export const getUserCapabilities = () => {
  return http().get<CapabilityWithUserConfigListResponse>('/capabilities/me');
};

/**
 * Get capability autocomplete suggestions for @mentions in SOP editor
 * @domain workspace
 */
export const getCapabilityAutocomplete = (query?: string) => {
  const params = query ? `?q=${encodeURIComponent(query)}` : '';
  return http().get<CapabilityAutocompleteResponse>(
    `/capabilities/autocomplete${params}`
  );
};

// --- D1 (ARCFE-1037): the eight user-record WRITE endpoints -----------------
// Carved verbatim from `src/api/scribeAPI.ts` (five, v1 client) and
// `src/api/scribeAPIV2.ts` (three, v2 client) — both hubs re-export
// name-intact. The three V2 writes were `@domain REVIEW`; the carve-time
// ruling (same P0.4 class as IP2's reads above) assigns them
// workspace/identity-projection: each writes the user's own record. Routes,
// verbs and body shapes are byte-identical and pinned in `user.test.ts`.
// NOTE `updateUserRegion`: only the ENDPOINT moves — its caller's mutationFn
// (`useMutateUpdateUserRegion`) is auth-infrastructure-entangled and stays an
// owned exclusion on the family record (D2's problem, not D1's).

/** @domain workspace */
export const updateUser = (user: UpdateUserRequest, signal?: AbortSignal) => {
  return httpV1().patch<UserResponse>('/user/', user, { signal });
};

/** @domain workspace */
export const updateUserPendingOnboardingTasks = (task: OnboardingTask) => {
  return httpV1().patch<UserResponse>('/user/onboarding-tasks/' + task);
};

/** @domain workspace */
export const updateUserPendingOnboardingSubtasks = (subTask: PendingSubStep) => {
  return httpV1().patch<UserResponse>('/user/onboarding-subtasks', {
    step: subTask.step,
    page: subTask.page,
  });
};

/** @domain workspace */
export const updateUserName = (input: {
  firstName: string | null;
  lastName: string | null;
}) => {
  const { firstName, lastName } = input;
  return httpV1().patch<UserResponse>(`/user/name`, {
    first_name: firstName,
    last_name: lastName,
  });
};

/** @domain workspace */
export const updateUserRegion = (region: Region) => {
  return httpV1().patch<boolean>(`/user/kinde-properties/region`, {
    region,
  });
};

/** @domain workspace */
export const createUser = (createUserRequest: CreateUserRequest) => {
  return http().post<UserResponse>('/user', createUserRequest);
};

/** @domain workspace */
export const toggleUserMfa = (input: { mfaEnabled: boolean }) => {
  return http().patch(`/user/mfa`, {
    mfa_method: MFAMethod.MFA_SMS_EMAIL,
    enable_mfa: input.mfaEnabled,
  });
};

/** @domain workspace */
export const userSelfOnboard = (input: SelfOnboardRequest) => {
  return http().post<SelfOnboardResponse>(`/user/self-onboard`, input);
};

// --- The plain `/user/` record read, and the MUL-1879 session-timeout pair ---
//
// `getUser` is the last unblocked read of this family's own resource. It is
// three lines of transport on the v1 client, and it sat in the hub only because
// the D1 carve (ARCFE-1037) was scoped to WRITES — the family note records it
// under "(e) the two user READS still in the hub … are outside a write arm".
//
// THE TWO SESSION-TIMEOUT FNS ARE A RULING, NOT A MOP-UP, and the argument is
// the asymmetry rather than the convenience. The family record excluded them on
// its own task-1.4 blocker ("session/auth lifecycle is out of `/data` by
// construction"). Measured, that reads the blocker one word too widely: task 1.4
// excludes the AUTH session — the Auth0/Kinde SDK, provider and token
// acquisition — and `/user/session-timeout` is not that. It is an INACTIVITY
// PREFERENCE stored on the user record, and the proof is already in this package:
// `organization.ts` has held `setOrganizationSessionTimeout` and
// `clearOrganizationSessionTimeout` since OT2b — the same MUL-1879 feature, the
// same verb pair, the same resource shape one level up. A family cannot hold the
// ORG half of a preference pair inside `/data` and its own USER half out of it on
// a lifecycle argument; the asymmetry was the anomaly, not the ownership. This is
// the OT4 reasoning that ruled `postCreateSessionForOtherUser` workspace's,
// applied to a resource this family indisputably owns.
//
// FALSIFIER, so overturning it is cheap: if either fn is shown to read or write
// AUTH state — a token, a provider session, a refresh — rather than a stored
// minutes value on the user record, it belongs back in the hub. At this head
// `set` sends `{ minutes }` and `clear` sends no body, and neither touches a
// token.
//
// ONLY TRANSPORT MOVES. Their callers keep whatever effects they hold; nothing
// about this carve changes a hook.

/** @domain workspace */
export const getUser = () => {
  return httpV1().get<UserResponse>(`/user/`);
};

/**
 * MUL-1879: set the caller's per-user session-inactivity timeout.
 * Returns the resolved {@link EffectiveSessionTimeout} (or `null` when
 * the caller is out of the Flagsmith cohort).
 * @domain workspace
 */
export const setUserSessionTimeout = (minutes: number) => {
  return http().patch<EffectiveSessionTimeout | null>(`/user/session-timeout`, {
    minutes,
  });
};

/**
 * MUL-1879: disable the caller's session-inactivity timeout. Clears any
 * stored value and sets the explicit-disabled flag, so the resolver
 * returns `source: USER_DISABLED` on the next read. Idempotent — calling
 * on an already-disabled user still returns 204. Refused with 409 when
 * the caller's organisation has set the value (org wins).
 * @domain workspace
 */
export const clearUserSessionTimeout = () => {
  return http().delete<void>(`/user/session-timeout`);
};
